import { HeroKicker } from '@/components/marketing/hero-kicker'
import { HeroTitle } from '@/components/marketing/hero-title'
import type { LegalDocument } from '@/config/marketing-content/legal-pages'

const LEGAL_LINK_PATTERN = /\[\[([^|\]]+)\|((?:\/|https?:\/\/)[^\]]+)\]\]/g

function LegalParagraph({ text }: { text: string }) {
  const nodes: React.ReactNode[] = []
  let lastIndex = 0

  for (const match of text.matchAll(LEGAL_LINK_PATTERN)) {
    const [fullMatch, label, href] = match
    const matchIndex = match.index ?? 0

    if (matchIndex > lastIndex) {
      nodes.push(text.slice(lastIndex, matchIndex))
    }

    nodes.push(
      <a
        key={`${href}-${matchIndex}`}
        href={href}
        {...(href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {label}
      </a>
    )

    lastIndex = matchIndex + fullMatch.length
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }

  return <p>{nodes}</p>
}

function LegalSectionHeading({
  level,
  title,
}: {
  level: 'h2' | 'h3'
  title: string
}) {
  const className = level === 'h3' ? 'legal-document__subheading' : 'legal-document__heading'
  const Tag = level

  return <Tag className={className}>{title}</Tag>
}

export function LegalDocumentLayout({ document }: { document: LegalDocument }) {
  return (
    <main className="legal-document">
      <div className="marketing-container">
        <header className="legal-document__header">
          <div className="hero-stack hero-stack--start">
            <HeroKicker>{document.eyebrow}</HeroKicker>
            <HeroTitle className="legal-document__title">{document.title}</HeroTitle>
            {document.lastUpdated ? (
              <p className="legal-document__updated">Last updated: {document.lastUpdated}</p>
            ) : null}
          </div>
        </header>

        <div className="legal-document__intro legal-document__prose">
          {document.introParagraphs.map((paragraph, index) => (
            <LegalParagraph key={`intro-${index}`} text={paragraph} />
          ))}
        </div>

        {document.sections.map((section) => (
          <section key={section.id} id={section.id} className="legal-document__section">
            <LegalSectionHeading level={section.headingLevel ?? 'h2'} title={section.title} />
            <div className="legal-document__section-body legal-document__prose">
              {section.paragraphs.map((paragraph, index) => (
                <LegalParagraph key={`${section.id}-${index}`} text={paragraph} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </main>
  )
}
